import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CustomButton from '../../components/common/CustomButton';
import { merchantAuthController } from "../../controllers/authController";
import { toast } from "react-toastify";

const VerifyEmailForm = () => {
	const navigate = useNavigate();
	const location: any = useLocation();
	const requestId = location.state?.requestId;
	const email = location.state?.email;
	const [otp, setOtp] = useState(["", "", "", ""]);
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
	const [loading, setLoading] = useState(false);
	const [resendLoading, setResendLoading] = useState(false);
	const [cooldown, setCooldown] = useState(0);
	const [newRequestId, setNewRequestId] = useState("");

	useEffect(() => {
		if (!requestId || !email) {
			toast.error("Invalid Request!");
			navigate("/register");
		}
	}, [navigate, requestId, email]);

	useEffect(() => {
		let timer: NodeJS.Timeout;
		if (cooldown > 0) {
			timer = setInterval(() => {
				setCooldown((prev) => prev - 1);
			}, 1000);
		}
		return () => {
			if (timer) clearInterval(timer);
		};
	}, [cooldown]);

	const formatTime = (seconds: number) => {
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
	};

	const focusInput = useCallback((index: number) => {
		setTimeout(() => {
			inputRefs.current[index]?.focus();
			inputRefs.current[index]?.select();
		}, 0);
	}, []);

	const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;

		// Handle paste
		if (value.length > 1) {
			const digits = value.replace(/\D/g, "").slice(0, 4).split("");
			const newOtp = [...otp];
			digits.forEach((digit, i) => {
				if (index + i < 4) {
					newOtp[index + i] = digit;
				}
			});
			setOtp(newOtp);
			const nextIndex = Math.min(index + digits.length, 3);
			focusInput(nextIndex);
			return;
		}

		// Handle single character
		const digit = value.replace(/\D/g, "");
		const newOtp = [...otp];
		newOtp[index] = digit;
		setOtp(newOtp);

		// Move to next input if digit entered
		if (digit && index < 3) {
			focusInput(index + 1);
		}
	};

	const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Backspace") {
			e.preventDefault();
			const newOtp = [...otp];

			if (otp[index]) {
				// Clear current box
				newOtp[index] = "";
				setOtp(newOtp);
			} else if (index > 0) {
				// Move to previous box and clear it
				newOtp[index - 1] = "";
				setOtp(newOtp);
				focusInput(index - 1);
			}
		} else if (e.key === "ArrowLeft" && index > 0) {
			e.preventDefault();
			focusInput(index - 1);
		} else if (e.key === "ArrowRight" && index < 3) {
			e.preventDefault();
			focusInput(index + 1);
		} else if (/^\d$/.test(e.key)) {
			// If typing a digit on a filled box, replace it
			e.preventDefault();
			const newOtp = [...otp];
			newOtp[index] = e.key;
			setOtp(newOtp);
			if (index < 3) {
				focusInput(index + 1);
			}
		}
	};

	const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		e.target.select();
	};

	const onSubmit = async (e: React.FormEvent) => {
		try {
			e.preventDefault();
			if (loading) return;

			const otpValue = otp.join("");
			if (otpValue.length !== 4) {
				toast.error("Please enter a complete 4-digit OTP");
				return;
			}

			setLoading(true);

			await merchantAuthController.verifyEmailOtp({
				requestId: newRequestId || requestId,
				otp: otpValue
			});

			toast.success("Email verification successful!");
			navigate("/login");
		} catch (error: any) {
			console.error(error);
			toast.error(error?.response?.data?.message || "Verification failed. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const onResendOTP = async () => {
		try {
			if (resendLoading || cooldown > 0) return;
			setResendLoading(true);

			const response = await merchantAuthController.resendEmailOtp({
				email
			});

			setNewRequestId(response.data?.requestId);
			toast.success("OTP has been resent successfully!");
			setCooldown(120);
		} catch (error: any) {
			console.error(error);
			toast.error(error?.response?.data?.message || "Failed to resend OTP. Please try again.");
		} finally {
			setResendLoading(false);
		}
	};

	return (
		<div className="md:w-1/2 w-full flex justify-center items-center px-4 md:px-12 py-8">
			<div className="w-full max-w-[480px] flex flex-col gap-6 text-black text-center">
				<h1 className="text-2xl md:text-4xl font-bold font-sk-modernist">
					Verify Email!
				</h1>

				<p className="text-sm md:text-base font-sk-modernist text-styola-gray">
					Please enter the One Time Password (OTP) that has been sent to your E-mail.
				</p>

				<form onSubmit={onSubmit} className="w-full flex flex-col gap-6">
					<div className="flex justify-center gap-3 md:gap-4">
						{otp.map((value, index) => (
							<input
								key={index}
								ref={(el) => (inputRefs.current[index] = el)}
								type="text"
								inputMode="numeric"
								maxLength={4}
								value={value}
								onChange={(e) => handleChange(index, e)}
								onKeyDown={(e) => handleKeyDown(index, e)}
								onFocus={handleFocus}
								autoFocus={index === 0}
								className="w-14 h-14 md:w-[72px] md:h-[72px] text-center text-2xl md:text-3xl font-bold border border-gray-300 rounded-lg focus:border-styola-primary focus:outline-none transition-colors font-sk-modernist"
							/>
						))}
					</div>

					<CustomButton
						title="Verify Email"
						variant="secondary"
						type="submit"
						className="w-full font-sk-modernist"
						isLoading={loading}
					/>
				</form>

				<p className="text-sm md:text-base text-center text-black font-sk-modernist">
					Didn't receive the OTP?{" "}
					<span
						className={cooldown > 0 ? "text-gray-400 cursor-not-allowed" : "text-styola-primary font-bold cursor-pointer hover:underline"}
						onClick={onResendOTP}
					>
						{resendLoading ? "Loading..." : cooldown > 0 ? `Resend in ${formatTime(cooldown)}` : "Resend OTP"}
					</span>
				</p>
			</div>
		</div>
	);
};

export default VerifyEmailForm;
